import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { featureFlags } from '@common/feature-flags'
import { useApiClient } from '../api-client-provider'
import { useTreatyQuery } from '../utils/use-treaty-query'

/**
 * Nth-purchase campaigns ("every X-th start at a shop gets Y% off"). The backend
 * decides who holds the discount; a client only asks for a hold when the
 * machines page opens (app, my.bubbles) or after kiosk login, shows the
 * banner while the hold lives, and hides coupon entry meanwhile. Prices come
 * discounted from the usual price endpoints while the hold lives.
 */
export function isNthPurchaseCampaignEnabled() {
  return !!featureFlags.nthPurchaseCampaign
}

export const NTH_PURCHASE_CAMPAIGN_QUERY_KEY = ['nth-purchase-campaign'] as const

const currentKey = (shopId: number | null, accountKey: string | null | undefined) =>
  [...NTH_PURCHASE_CAMPAIGN_QUERY_KEY, 'current', shopId, accountKey ?? null] as const

export type NthPurchaseCampaignHold = {
  holdId: number
  nthPurchaseCampaignId: number
  shopId: number
  percentage: number
  everyNth: number
  expiresAt: Date
  extended: boolean
  title: string
  body: string
}

const toHold = (hold: NthPurchaseCampaignHold | null | undefined) =>
  hold ? { ...hold, expiresAt: new Date(hold.expiresAt) } : null

/** Prices depend on the hold, so whenever it appears or goes away the machine lists must be refetched. */
function invalidatePrices(queryClient: ReturnType<typeof useQueryClient>, shopId: number | null) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['shopDetails', shopId] }),
    queryClient.invalidateQueries({ queryKey: ['machineStatusList', shopId] }),
  ])
}

/**
 * Grant: ask for the hold once per visit of the machines page (refetched on
 * every mount, never in the background, so an expired hold is not grabbed
 * straight back by the same open page). The answer seeds useNthPurchaseCampaignHold.
 * `accountKey` (user id or auth token) disables it while signed out.
 */
export function useGrantNthPurchaseCampaignHold(shopId: number | null, accountKey: string | null | undefined) {
  const client = useApiClient()
  const queryClient = useQueryClient()
  return useTreatyQuery({
    queryKey: [...NTH_PURCHASE_CAMPAIGN_QUERY_KEY, 'grant', shopId, accountKey ?? null],
    queryFn: async () => {
      const response = await client['nth-purchase-campaign'].grant.post({ shopId: shopId! })
      if (response.data) {
        const hold = toHold(response.data.hold as NthPurchaseCampaignHold | null)
        const previous = queryClient.getQueryData<{ hold: NthPurchaseCampaignHold | null }>(currentKey(shopId, accountKey))
        queryClient.setQueryData(currentKey(shopId, accountKey), { hold })
        if (previous?.hold?.holdId !== hold?.holdId) {
          await invalidatePrices(queryClient, shopId)
        }
      }
      return response
    },
    enabled: isNthPurchaseCampaignEnabled() && !!shopId && !!accountKey,
    staleTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/**
 * The user's live hold at the shop (null when none), without granting one.
 * Polls, so a re-grant after a failed payment or an expiry shows up.
 */
export function useNthPurchaseCampaignHold(shopId: number | null, accountKey: string | null | undefined) {
  const client = useApiClient()
  const queryClient = useQueryClient()
  const query = useTreatyQuery({
    queryKey: currentKey(shopId, accountKey),
    queryFn: async () => {
      const response = await client['nth-purchase-campaign'].current.get({ query: { shopId: shopId! } })
      const previous = queryClient.getQueryData<{ hold: NthPurchaseCampaignHold | null }>(currentKey(shopId, accountKey))
      const hold = response.data?.hold as NthPurchaseCampaignHold | null | undefined
      if (response.data && previous && previous.hold?.holdId !== hold?.holdId) {
        await invalidatePrices(queryClient, shopId)
      }
      return response
    },
    enabled: isNthPurchaseCampaignEnabled() && !!shopId && !!accountKey,
    // poll, and ask again right when the hold should run out
    refetchInterval: q => {
      const expiresAt = (q.state.data as { hold?: NthPurchaseCampaignHold | null } | undefined)?.hold?.expiresAt
      const untilExpiry = expiresAt ? new Date(expiresAt).getTime() - Date.now() + 500 : Infinity
      return Math.max(1000, Math.min(15_000, untilExpiry))
    },
    retry: false,
  })
  const hold = toHold(query.data?.hold as NthPurchaseCampaignHold | null | undefined)
  const secondsLeft = useSecondsLeft(hold?.expiresAt)
  return { ...query, hold: hold && secondsLeft > 0 ? hold : null, secondsLeft: Math.max(secondsLeft, 0) }
}

function useSecondsLeft(expiresAt: Date | undefined) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!expiresAt) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [expiresAt?.getTime()])
  return expiresAt ? Math.ceil((expiresAt.getTime() - now) / 1000) : 0
}

/** "4:05" */
export function formatHoldCountdown(secondsLeft: number) {
  const minutes = Math.floor(secondsLeft / 60)
  const seconds = secondsLeft % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}
